import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useCreateDuelMutation, useRecentDuels } from "../../../lib/api/fantasy-fams";
import { listMyFams } from "../../../lib/api";
import { getCurrentUser, type CurrentUser } from "../../../lib/auth";
import { getFamBySlug } from "../../../lib/api";
import { formatPctSigned, pctColorClass } from "./_shared";
import type { DuelCadence, Duel, FamSummary } from "../../../lib/types";

// /fantasy/duels — recent duel feed + "Challenge a Fam" entry point for PMs.
//
// The challenger PM picks an opponent slug + cadence; the backend resolves
// the slug, builds a `forming` duel, and emits a notification to the opponent
// PM. Until accepted (24h window) the duel sits in `forming`.
export function DuelsListRoute() {
	const recent = useRecentDuels(20);
	const [user, setUser] = useState<CurrentUser | null>(null);
	const [myFams, setMyFams] = useState<FamSummary[]>([]);
	const [modalOpen, setModalOpen] = useState(false);

	useEffect(() => {
		void getCurrentUser().then(setUser);
		void listMyFams()
			.then(setMyFams)
			.catch(() => setMyFams([]));
	}, []);

	// Only PMs can issue challenges. We look at the caller's role on each Fam
	// they belong to and offer the challenge button if any of those rows have
	// role=pm. The challenger Fam is picked inside the modal.
	const pmFams = useMemo(() => myFams.filter((f) => f.role === "pm"), [myFams]);
	const canChallenge = !!user && pmFams.length > 0;
	const mySlugs = useMemo(() => new Set(myFams.map((f) => f.slug)), [myFams]);

	return (
		<div className="mx-auto max-w-4xl space-y-6 px-4 py-8">
			<header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
				<div>
					<Link
						to="/fantasy"
						className="text-xs text-[var(--color-muted)] hover:text-[var(--color-fg)]"
					>
						← Fantasy
					</Link>
					<h1 className="mt-1 text-2xl font-bold">Fantasy Fams: Duels</h1>
					<p className="text-sm text-[var(--color-muted)]">
						PM vs PM. Pick a cadence, pick a wager, beat them in public.
					</p>
				</div>
				{canChallenge && (
					<button
						type="button"
						onClick={() => setModalOpen(true)}
						className="rounded-md bg-[var(--color-accent)] px-4 py-2 text-sm font-semibold text-black hover:bg-emerald-400"
					>
						Challenge a Fam
					</button>
				)}
			</header>

			{recent.isLoading && (
				<p className="text-sm text-[var(--color-muted)]">Loading duels…</p>
			)}
			{recent.isError && (
				<p className="text-sm text-[var(--color-danger)]">
					Could not load duels.{" "}
					{recent.error instanceof Error ? recent.error.message : ""}
				</p>
			)}

			{recent.data && (
				<section className="space-y-3">
					{recent.data.duels.length === 0 ? (
						<div className="rounded-lg border border-dashed border-[var(--color-border)] bg-[var(--color-bg-elev)] p-6 text-center">
							<p className="text-sm text-[var(--color-muted)]">
								No duels yet. Be the first to throw down — every Fam shows
								up here once a challenge lands.
							</p>
						</div>
					) : (
						recent.data.duels.map((duel) => (
							<DuelRow key={duel.id} duel={duel} mySlugs={mySlugs} />
						))
					)}
				</section>
			)}

			{modalOpen && (
				<ChallengeModal myFams={pmFams} onClose={() => setModalOpen(false)} />
			)}
		</div>
	);
}

// --- Single row -------------------------------------------------------------

function DuelRow({ duel, mySlugs }: { duel: Duel; mySlugs: Set<string> }) {
	const youInvolved =
		mySlugs.has(duel.challenger.famSlug) || mySlugs.has(duel.opponent.famSlug);
	const statusClass =
		duel.status === "completed"
			? duel.winnerFamId
				? "border-[var(--color-accent)]/40 text-[var(--color-accent)]"
				: "border-[var(--color-border)] text-[var(--color-muted)]"
			: duel.status === "active"
				? "border-amber-500/40 text-amber-300"
				: duel.status === "forming"
					? "border-[var(--color-border)] text-[var(--color-muted)]"
					: "border-[var(--color-border)] text-[var(--color-muted)]";

	return (
		<Link
			to={`/duels/${encodeURIComponent(duel.id)}`}
			className={`block rounded-lg border bg-[var(--color-bg-card)] p-4 transition hover:border-[var(--color-accent)] ${
				youInvolved
					? "border-[var(--color-accent)]/40"
					: "border-[var(--color-border)]"
			}`}
		>
			<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
				<div className="flex min-w-0 flex-1 items-center gap-3">
					<DuelSide
						famName={duel.challenger.famName}
						famSlug={duel.challenger.famSlug}
						returnPct={duel.challenger.returnPct}
						winner={duel.winnerFamId === duel.challenger.famId}
					/>
					<span aria-hidden className="text-lg text-[var(--color-muted)]">
						⚔
					</span>
					<DuelSide
						famName={duel.opponent.famName}
						famSlug={duel.opponent.famSlug}
						returnPct={duel.opponent.returnPct}
						winner={duel.winnerFamId === duel.opponent.famId}
					/>
				</div>
				<div className="flex flex-col items-end gap-1">
					<span
						className={`rounded-md border px-2 py-0.5 text-[10px] uppercase tracking-wider ${statusClass}`}
					>
						{duel.status}
					</span>
					<span className="text-xs text-[var(--color-muted)]">
						{duel.cadence}
					</span>
				</div>
			</div>
		</Link>
	);
}

function DuelSide({
	famName,
	famSlug,
	returnPct,
	winner,
}: {
	famName: string;
	famSlug: string;
	returnPct?: number;
	winner: boolean;
}) {
	return (
		<div className="min-w-0 flex-1">
			<p
				className={`truncate text-sm font-semibold ${
					winner ? "text-[var(--color-accent)]" : ""
				}`}
			>
				{winner && <span aria-hidden>👑 </span>}
				{famName}
			</p>
			<p className="truncate font-mono text-[11px] text-[var(--color-muted)]">
				{famSlug}
			</p>
			{returnPct !== undefined && (
				<p className={`font-mono text-xs ${pctColorClass(returnPct)}`}>
					{formatPctSigned(returnPct)}
				</p>
			)}
		</div>
	);
}

// --- Challenge modal --------------------------------------------------------

interface ChallengeModalProps {
	myFams: FamSummary[];
	onClose: () => void;
}

function ChallengeModal({ myFams, onClose }: ChallengeModalProps) {
	const create = useCreateDuelMutation();
	const [challengerSlug, setChallengerSlug] = useState(myFams[0]?.slug ?? "");
	const [opponentSlug, setOpponentSlug] = useState("");
	const [cadence, setCadence] = useState<DuelCadence>("weekly");
	const [wager, setWager] = useState("");
	const [resolveError, setResolveError] = useState<string | null>(null);

	async function onSubmit(e: React.FormEvent) {
		e.preventDefault();
		setResolveError(null);
		const cleaned = opponentSlug.trim().toLowerCase();
		if (!cleaned) {
			setResolveError("Pick an opponent Fam.");
			return;
		}
		if (cleaned === challengerSlug.trim().toLowerCase()) {
			setResolveError("Can't challenge your own Fam.");
			return;
		}

		// Validate the slug resolves to a Fam before issuing the challenge —
		// keeps the error inline rather than surfacing as a 404 after submit.
		try {
			await getFamBySlug(cleaned);
		} catch (err) {
			setResolveError(
				`No Fam with slug "${cleaned}". ${
					err instanceof Error ? err.message : ""
				}`,
			);
			return;
		}

		try {
			const res = await create.mutateAsync({
				opponentFamSlug: cleaned,
				cadence,
				wager: wager.trim() ? wager.trim() : undefined,
			});
			// Navigate to the new duel's spectator page so the challenger can
			// share the link immediately.
			window.location.assign(`/duels/${encodeURIComponent(res.duelId)}`);
		} catch {
			// Surface error via create.error below.
		}
	}

	return (
		<div
			role="dialog"
			aria-modal="true"
			aria-label="Challenge a Fam"
			className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
			onClick={(e) => {
				if (e.target === e.currentTarget) onClose();
			}}
		>
			<div className="w-full max-w-md space-y-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-5">
				<div className="flex items-start justify-between gap-3">
					<div>
						<h2 className="text-lg font-semibold">Challenge a Fam</h2>
						<p className="text-xs text-[var(--color-muted)]">
							Their PM has 24h to accept. Lineups lock at acceptance.
						</p>
					</div>
					<button
						type="button"
						onClick={onClose}
						className="rounded-md border border-[var(--color-border)] px-2 py-1 text-xs hover:border-[var(--color-accent)]"
					>
						Close
					</button>
				</div>

				<form onSubmit={onSubmit} className="space-y-3">
					<label className="block text-xs text-[var(--color-muted)]">
						Challenger Fam
						<select
							value={challengerSlug}
							onChange={(e) => setChallengerSlug(e.target.value)}
							className="mt-1 block w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elev)] px-2 py-1.5 text-sm text-[var(--color-fg)]"
						>
							{myFams.map((f) => (
								<option key={f.slug} value={f.slug}>
									{f.name} ({f.slug})
								</option>
							))}
						</select>
					</label>

					<label className="block text-xs text-[var(--color-muted)]">
						Opponent Fam slug
						<input
							type="text"
							value={opponentSlug}
							onChange={(e) => setOpponentSlug(e.target.value)}
							placeholder="tiger-cub-capital"
							className="mt-1 block w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elev)] px-2 py-1.5 font-mono text-sm text-[var(--color-fg)]"
						/>
					</label>

					<label className="block text-xs text-[var(--color-muted)]">
						Cadence
						<select
							value={cadence}
							onChange={(e) => setCadence(e.target.value as DuelCadence)}
							className="mt-1 block w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elev)] px-2 py-1.5 text-sm text-[var(--color-fg)]"
						>
							<option value="weekly">Weekly (5 trading days)</option>
							<option value="monthly">Monthly (~21 trading days)</option>
							<option value="quarterly">
								Quarterly (~63 trading days)
							</option>
						</select>
					</label>

					<label className="block text-xs text-[var(--color-muted)]">
						Wager (optional)
						<input
							type="text"
							value={wager}
							onChange={(e) => setWager(e.target.value)}
							placeholder="Loser posts the L on X"
							className="mt-1 block w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elev)] px-2 py-1.5 text-sm text-[var(--color-fg)]"
						/>
					</label>

					{resolveError && (
						<p className="text-xs text-[var(--color-danger)]">
							{resolveError}
						</p>
					)}
					{create.error && (
						<p className="text-xs text-[var(--color-danger)]">
							{create.error.code}: {create.error.message}
						</p>
					)}

					<div className="flex items-center justify-end gap-2 pt-1">
						<button
							type="button"
							onClick={onClose}
							className="rounded-md border border-[var(--color-border)] px-3 py-1.5 text-sm hover:border-[var(--color-accent)]"
						>
							Cancel
						</button>
						<button
							type="submit"
							disabled={create.isPending}
							className="rounded-md bg-[var(--color-accent)] px-3 py-1.5 text-sm font-semibold text-black hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-50"
						>
							{create.isPending ? "Sending…" : "Send challenge"}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
}
